import { type SignInInput, signInSchema } from '@chipn/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, InputField } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { authErrorMessage, callbackUrl, safeRedirect } from './auth-helpers';
import { AuthLayout, FormError } from './AuthLayout';

export function SignInPage() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/sign-in' });
  const [error, setError] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const form = useForm<SignInInput>({ resolver: zodResolver(signInSchema) });

  const submit = form.handleSubmit(async ({ email, password }) => {
    setError(null);
    setUnconfirmed(null);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) {
      if (/email not confirmed/i.test(err.message)) setUnconfirmed(email);
      return setError(authErrorMessage(err.message));
    }
    await navigate({ to: safeRedirect(redirect) });
  });

  const resend = async () => {
    if (!unconfirmed) return;
    const { error: err } = await supabase.auth.resend({
      type: 'signup',
      email: unconfirmed,
      options: { emailRedirectTo: callbackUrl(safeRedirect(redirect)) },
    });
    if (err) return setError(authErrorMessage(err.message));
    setResent(true);
  };

  return (
    <AuthLayout>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <h1 className="text-xl font-semibold">Sign in</h1>
        <InputField
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoFocus
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        <InputField
          label="Password"
          type="password"
          autoComplete="current-password"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <FormError message={error} />
        {unconfirmed && (
          <Button variant="ghost" onClick={() => void resend()} disabled={resent}>
            {resent ? 'Confirmation email sent' : 'Send the confirmation email again'}
          </Button>
        )}
        <Button type="submit" block loading={form.formState.isSubmitting}>
          Sign in
        </Button>
        <div className="flex flex-wrap justify-between gap-2 text-sm">
          <Link to="/forgot-password" className="font-medium text-text underline">
            Forgot password?
          </Link>
          <Link to="/sign-up" search={{ redirect }} className="font-medium text-text underline">
            Create an account
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
